<template>
  <div class="user-email-card">
    <div class="user-email-card__header">
      <div>
        <h2>
          Your Email Verification
        </h2>
        <p>
          Verify your email to receive reports and eligible alerts.
        </p>
      </div>
      <hs-badge :tone="statusTone" icon="mail">
        {{ statusLabel }}
      </hs-badge>
    </div>

    <div class="user-email-card__primary">
      <p>
        Primary contact email
      </p>
      <strong>
        {{ primaryEmailRecord?.email || "No email on file" }}
      </strong>
      <span>
        {{ verifiedCaption }}
      </span>
    </div>

    <div class="user-email-card__list">
      <div class="user-email-card__list-header">
        <p>
          Email addresses
        </p>
        <span>
          {{ emailRecords.length }} total
        </span>
      </div>

      <div v-if="emailRecords.length" class="user-email-card__records">
        <article
          v-for="record in emailRecords"
          :key="record.id || record.email"
          class="user-email-record"
        >
          <div class="user-email-record__top">
            <div>
              <p>
                {{ record.email }}
              </p>
              <span>
                {{ getEmailCaption(record) }}
              </span>
            </div>
            <div class="user-email-record__badges">
              <hs-badge
                v-if="record.is_primary"
                tone="primary"
                size="sm"
              >
                Primary
              </hs-badge>
              <hs-badge :tone="record.is_verified ? 'success' : 'warning'" size="sm">
                {{ record.is_verified ? "Verified" : "Pending" }}
              </hs-badge>
            </div>
          </div>

          <div class="user-email-record__actions">
            <hs-button
              v-if="!record.is_verified"
              size="sm"
              icon="send"
              @click="startVerification(record)"
            >
              Verify
            </hs-button>
            <hs-button
              v-if="!record.is_primary"
              variant="secondary"
              size="sm"
              icon="star"
              @click="setPrimary(record)"
            >
              Make primary
            </hs-button>
            <hs-button
              variant="danger"
              size="sm"
              icon="trash-2"
              @click="promptRemove(record)"
            >
              Remove
            </hs-button>
          </div>
        </article>
      </div>

      <div
        v-else
        class="user-email-card__empty"
      >
        Add your first email address to receive account notifications.
      </div>
    </div>

    <div v-if="currentStep !== 'idle'" class="user-email-flow">
      <div class="user-email-flow__steps">
        <div v-for="step in steps" :key="step.id" class="user-email-flow__step-wrap">
          <div
            class="user-email-flow__step"
            :class="getStepClass(step.id)"
          >
            <hs-icon v-if="step.id < currentStepNumber && step.id !== 3" name="check" :size="13" />
            <span v-else>{{ step.id }}</span>
          </div>
          <div
            v-if="step.id < steps.length"
            class="user-email-flow__line"
          ></div>
        </div>
      </div>

      <hs-alert
        v-if="currentStep === 'confirm'"
        tone="info"
        title="Confirm email address"
      >
        <p>
          We'll send a 6-digit verification code to
          <strong>{{ verificationTargetEmail }}</strong>.
        </p>
        <div class="user-email-flow__actions">
          <hs-button
            icon="send"
            :loading="requesting"
            :disabled="requesting"
            @click="sendVerificationCode"
          >
            Send verification code
          </hs-button>
          <hs-button variant="secondary" @click="cancelVerification">
            Cancel
          </hs-button>
        </div>
      </hs-alert>

      <hs-alert
        v-if="currentStep === 'verify'"
        tone="success"
        title="Code sent"
      >
        <p>
          Enter the 6-digit code sent to
          <strong>{{ verificationTargetEmail }}</strong>.
        </p>
        <p
          v-if="retryCountdown > 0"
          class="user-email-flow__hint"
        >
          Request new code in {{ retryCountdown }}s
        </p>
        <hs-button
          v-else
          variant="ghost"
          size="sm"
          @click="sendVerificationCode"
        >
          Resend code
        </hs-button>

        <div class="user-email-flow__actions">
      <hs-input
            v-model="code"
            type="text"
            maxlength="6"
            placeholder="000000"
            class="user-email-flow__code"
            @keyup.enter="verifyCode"
          />
          <hs-button
            variant="success"
            icon="check"
            :loading="verifying"
            :disabled="verifying || code.length !== 6"
            @click="verifyCode"
          >
            Verify code
          </hs-button>
          <hs-button variant="secondary" @click="cancelVerification">
            Cancel
          </hs-button>
        </div>
      </hs-alert>

      <hs-alert
        v-if="currentStep === 'verified'"
        tone="success"
        :message="`${verificationTargetEmail} is now verified.`"
      />
    </div>

    <div class="user-email-card__footer">
      <hs-button variant="secondary" icon="plus" @click="openAddModal">
        Add email
      </hs-button>
    </div>

    <p v-if="feedback" class="mt-4 text-sm" :class="feedbackClass">
      {{ feedback }}
    </p>

    <email-edit-modal
      v-if="editModalOpen"
      :entity-label="'email address'"
      entity-name="Your account"
      :current-email="''"
      :loading="updating"
      :server-error="updateError"
      @close="closeEditModal"
      @save="saveEmail"
    />

    <confirmation-modal
      :is-open="confirmRemoval"
      title="Remove email address"
      :message="removeMessage"
      confirm-text="Remove email"
      cancel-text="Cancel"
      type="danger"
      :loading="removing"
      @confirm="removeEmail"
      @close="closeRemoveModal"
    />
  </div>
</template>

<script>
import { computed, ref, watch, onUnmounted } from "vue";
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import EmailEditModal from "@/components/admin/email/EmailEditModal.vue";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import { HsAlert, HsBadge, HsButton, HsIcon, HsInput } from "@/components/ui";

export default {
  name: "UserEmailVerificationCard",
  components: {
    EmailEditModal,
    ConfirmationModal,
    HsAlert,
    HsBadge,
    HsButton,
    HsIcon,
    HsInput,
  },
  props: {
    email: {
      type: String,
      default: "",
    },
    emailVerified: {
      type: Boolean,
      default: false,
    },
    emailVerifiedAt: {
      type: [String, Date],
      default: null,
    },
    emails: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["status-changed"],
  setup(props, { emit }) {
    const currentStep = ref("idle");
    const requesting = ref(false);
    const verifying = ref(false);
    const updating = ref(false);
    const removing = ref(false);
    const feedback = ref("");
    const feedbackVariant = ref("info");
    const code = ref("");
    const editModalOpen = ref(false);
    const updateError = ref("");
    const confirmRemoval = ref(false);
    const retryCountdown = ref(0);
    const selectedEmailRecord = ref(null);
    const emailToRemove = ref(null);
    let retryTimer = null;

    const steps = [
      { id: 1, label: "Confirm Email" },
      { id: 2, label: "Enter Code" },
      { id: 3, label: "Verified" },
    ];

    const emailRecords = computed(() => {
      if (Array.isArray(props.emails) && props.emails.length) {
        return props.emails;
      }

      if (!props.email) {
        return [];
      }

      return [
        {
          id: "primary-fallback",
          email: props.email,
          is_primary: true,
          is_verified: props.emailVerified,
          verified_at: props.emailVerifiedAt,
        },
      ];
    });

    const primaryEmailRecord = computed(
      () =>
        emailRecords.value.find((record) => record.is_primary) ||
        emailRecords.value[0] ||
        null,
    );

    const verificationTargetEmail = computed(
      () =>
        selectedEmailRecord.value?.email ||
        primaryEmailRecord.value?.email ||
        "",
    );

    const currentStepNumber = computed(() => {
      if (currentStep.value === "confirm") return 1;
      if (currentStep.value === "verify") return 2;
      if (currentStep.value === "verified") return 3;
      return 0;
    });

    const statusLabel = computed(() => {
      if (!primaryEmailRecord.value) {
        return "No email";
      }
      return primaryEmailRecord.value.is_verified
        ? "Verified"
        : "Pending verification";
    });

    const statusTone = computed(() => {
      if (!primaryEmailRecord.value) {
        return "neutral";
      }
      return primaryEmailRecord.value.is_verified ? "success" : "warning";
    });

    const verifiedCaption = computed(() => {
      if (!primaryEmailRecord.value) {
        return "Add an email address to receive account notifications.";
      }
      if (
        primaryEmailRecord.value.is_verified &&
        primaryEmailRecord.value.verified_at
      ) {
        return `Verified on ${formatDate(
          primaryEmailRecord.value.verified_at,
        )}`;
      }
      return "Verification pending.";
    });

    const feedbackClass = computed(() => {
      if (feedbackVariant.value === "error") {
        return "text-red-600 dark:text-red-300";
      }
      if (feedbackVariant.value === "success") {
        return "text-emerald-600 dark:text-emerald-300";
      }
      return "text-gray-600 dark:text-gray-400";
    });

    const removeMessage = computed(() => {
      if (!emailToRemove.value?.email) {
        return "This will remove the selected email from your account.";
      }
      return `Remove ${emailToRemove.value.email} from your account? You will stop receiving notifications on that inbox until you add it again.`;
    });

    const getStepClass = (stepId) => {
      if (stepId < currentStepNumber.value) {
        return "step-complete";
      }
      if (stepId === currentStepNumber.value) {
        return "step-active";
      }
      return "step-idle";
    };

    const getEmailCaption = (record) => {
      if (record.is_verified && record.verified_at) {
        return `Verified on ${formatDate(record.verified_at)}`;
      }
      return record.is_primary
        ? "Primary email. Verification pending."
        : "Secondary email. Verification pending.";
    };

    watch(
      () => props.emails,
      () => {
        feedback.value = "";
        if (
          selectedEmailRecord.value &&
          !emailRecords.value.some(
            (record) => record.id === selectedEmailRecord.value?.id,
          )
        ) {
          selectedEmailRecord.value = null;
          currentStep.value = "idle";
        }
      },
      { deep: true },
    );

    const setFeedback = (message, variant = "info") => {
      feedback.value = message;
      feedbackVariant.value = variant;
    };

    const startCountdown = () => {
      retryCountdown.value = 60;
      if (retryTimer) clearInterval(retryTimer);

      retryTimer = setInterval(() => {
        retryCountdown.value -= 1;
        if (retryCountdown.value <= 0) {
          clearInterval(retryTimer);
          retryTimer = null;
        }
      }, 1000);
    };

    const startVerification = (record) => {
      if (!record?.id || !record?.email) {
        toast.error("No email address to verify.");
        return;
      }
      selectedEmailRecord.value = record;
      currentStep.value = "confirm";
      setFeedback("", "info");
    };

    const sendVerificationCode = async () => {
      if (!selectedEmailRecord.value?.id) {
        toast.error("Select an email address to verify.");
        return;
      }

      requesting.value = true;
      setFeedback("", "info");
      try {
        const response = await http.post(
          `/v1/user/emails/${selectedEmailRecord.value.id}/request-verification`,
        );
        const payload = response?.data || {};
        if (payload.message === "Email is already verified.") {
          toast.success(payload.message);
          emit("status-changed");
          currentStep.value = "idle";
          return;
        }

        toast.success("Verification code sent to your email.");
        currentStep.value = "verify";
        startCountdown();
        setFeedback(
          "Check your inbox for the 6-digit code. It expires in 15 minutes.",
          "success",
        );
      } catch (error) {
        console.error("Failed to send verification code", error);
        const message =
          error?.response?.data?.error?.message ||
          "Unable to send verification code.";
        toast.error(message);
        setFeedback(message, "error");
      } finally {
        requesting.value = false;
      }
    };

    const verifyCode = async () => {
      if (!selectedEmailRecord.value?.id) {
        toast.error("Select an email address to verify.");
        return;
      }

      const value = code.value.trim();
      if (value.length !== 6) {
        toast.error("Enter the 6-digit code sent to your email.");
        return;
      }

      verifying.value = true;
      setFeedback("", "info");
      try {
        await http.post(
          `/v1/user/emails/${selectedEmailRecord.value.id}/verify`,
          {
            verification_code: value,
          },
        );
        toast.success("Email verified successfully.");
        currentStep.value = "verified";
        code.value = "";
        emit("status-changed");

        if (retryTimer) {
          clearInterval(retryTimer);
          retryTimer = null;
        }

        setTimeout(() => {
          currentStep.value = "idle";
          selectedEmailRecord.value = null;
        }, 2000);
      } catch (error) {
        console.error("Failed to verify email", error);
        const message =
          error?.response?.data?.error?.message ||
          "Verification failed. Check your code and try again.";
        toast.error(message);
        setFeedback(message, "error");
      } finally {
        verifying.value = false;
      }
    };

    const cancelVerification = () => {
      currentStep.value = "idle";
      code.value = "";
      selectedEmailRecord.value = null;
      if (retryTimer) {
        clearInterval(retryTimer);
        retryTimer = null;
      }
      setFeedback("", "info");
    };

    const openAddModal = () => {
      editModalOpen.value = true;
      updateError.value = "";
    };

    const closeEditModal = () => {
      editModalOpen.value = false;
      updateError.value = "";
    };

    const saveEmail = async (newEmail) => {
      updating.value = true;
      updateError.value = "";
      try {
        const response = await http.post("/v1/user/emails", {
          email: newEmail,
          set_as_primary: emailRecords.value.length === 0,
        });
        const payload = response?.data || {};
        const savedRecord = payload.email_record || null;

        toast.success("Email saved successfully.");
        closeEditModal();
        emit("status-changed");

        if (savedRecord && !savedRecord.is_verified) {
          selectedEmailRecord.value = savedRecord;
          currentStep.value = "confirm";
        }
      } catch (error) {
        console.error("Failed to save email", error);
        updateError.value =
          error?.response?.data?.error?.message ||
          "Unable to save email address.";
      } finally {
        updating.value = false;
      }
    };

    const setPrimary = async (record) => {
      try {
        await http.put(`/v1/user/emails/${record.id}/primary`);
        toast.success(`${record.email} is now your primary email.`);
        emit("status-changed");
      } catch (error) {
        console.error("Failed to update primary email", error);
        const message =
          error?.response?.data?.error?.message ||
          "Unable to set the primary email.";
        toast.error(message);
      }
    };

    const promptRemove = (record) => {
      emailToRemove.value = record;
      confirmRemoval.value = true;
    };

    const closeRemoveModal = () => {
      confirmRemoval.value = false;
      emailToRemove.value = null;
    };

    const removeEmail = async () => {
      if (!emailToRemove.value?.id) {
        closeRemoveModal();
        return;
      }

      removing.value = true;
      setFeedback("", "info");
      try {
        await http.delete(`/v1/user/emails/${emailToRemove.value.id}`);
        toast.success("Email removed from your account.");
        closeRemoveModal();
        currentStep.value = "idle";
        selectedEmailRecord.value = null;
        emit("status-changed");
      } catch (error) {
        console.error("Failed to remove email", error);
        const message =
          error?.response?.data?.error?.message || "Unable to remove email.";
        toast.error(message);
        setFeedback(message, "error");
      } finally {
        removing.value = false;
      }
    };

    onUnmounted(() => {
      if (retryTimer) {
        clearInterval(retryTimer);
      }
    });

    return {
      currentStep,
      currentStepNumber,
      steps,
      requesting,
      verifying,
      updating,
      removing,
      feedback,
      feedbackClass,
      code,
      retryCountdown,
      statusLabel,
      statusTone,
      verifiedCaption,
      emailRecords,
      primaryEmailRecord,
      verificationTargetEmail,
      getStepClass,
      getEmailCaption,
      startVerification,
      sendVerificationCode,
      verifyCode,
      cancelVerification,
      editModalOpen,
      openAddModal,
      closeEditModal,
      saveEmail,
      updateError,
      confirmRemoval,
      removeMessage,
      promptRemove,
      closeRemoveModal,
      removeEmail,
      setPrimary,
    };
  },
};

function formatDate(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
</script>

<style scoped>
.user-email-card {
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 94%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  box-shadow: none;
  display: grid;
  gap: 1rem;
  padding: 1rem;
}

.user-email-card__header,
.user-email-record__top,
.user-email-card__list-header {
  align-items: flex-start;
  display: flex;
  gap: 0.85rem;
  justify-content: space-between;
}

.user-email-card__header h2 {
  color: var(--hs-text, #0f172a);
  font-size: 1.05rem;
  font-weight: 950;
  margin: 0;
}

.user-email-card__header p,
.user-email-card__primary span,
.user-email-record span,
.user-email-card__list-header span,
.user-email-flow__hint {
  color: var(--hs-muted, #64748b);
  font-size: 0.82rem;
  font-weight: 750;
  line-height: 1.5;
}

.user-email-card__primary,
.user-email-record {
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 70%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  padding: 1rem;
}

.user-email-card__primary p,
.user-email-card__list-header p {
  color: var(--hs-muted, #64748b);
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.08em;
  margin: 0;
  text-transform: uppercase;
}

.user-email-card__primary strong,
.user-email-record p {
  color: var(--hs-text, #0f172a);
  display: block;
  font-size: 0.95rem;
  font-weight: 950;
  margin: 0.24rem 0;
  overflow-wrap: anywhere;
}

.user-email-card__list,
.user-email-card__records,
.user-email-flow {
  display: grid;
  gap: 0.75rem;
}

.user-email-record__badges,
.user-email-record__actions,
.user-email-flow__actions,
.user-email-card__footer {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.user-email-card__empty {
  border: 1px dashed var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  color: var(--hs-muted, #64748b);
  font-size: 0.86rem;
  font-weight: 800;
  padding: 1rem;
}

.user-email-flow__steps,
.user-email-flow__step-wrap {
  align-items: center;
  display: flex;
}

.user-email-flow__step {
  align-items: center;
  border: 2px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 999px;
  display: flex;
  font-size: 0.8rem;
  font-weight: 950;
  height: 2rem;
  justify-content: center;
  width: 2rem;
}

.user-email-flow__line {
  background: var(--hs-border, rgba(148, 163, 184, 0.22));
  height: 2px;
  margin: 0 0.45rem;
  width: 2rem;
}

.step-complete {
  background: #22c55e;
  border-color: #22c55e;
  color: #ffffff;
}

.step-active {
  background: var(--hs-primary, #3293ec);
  border-color: var(--hs-primary, #3293ec);
  color: #ffffff;
}

.step-idle {
  background: var(--hs-surface, #ffffff);
  color: var(--hs-muted, #64748b);
}

.user-email-flow__code {
  max-width: 10rem;
}

.user-email-flow__code :deep(input) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 1.05rem;
  letter-spacing: 0.16em;
  text-align: center;
}

@media (max-width: 640px) {
  .user-email-card__header,
  .user-email-record__top,
  .user-email-card__list-header {
    display: grid;
  }
}
</style>
